import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { ChatInferencerType, ChatRow, NamirasoftInferenceMetaDatabase } from "namirasoft-inference";
import { NSButton, NSLine, NSLoading } from "namirasoft-site-react";
import { Component, createRef, UIEvent } from "react";
import { InferenceChatTargetType } from "../InferenceRoutes";
import { InferenceRouterProps } from "../InferenceRouterProps";
import Styles from "./ChatSidebar.module.css";

const PAGE_SIZE = 100;

function getDateGroupLabel(date: Date): string
{
    let now = new Date();
    let today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0)
        return "Today";
    if (diffDays === 1)
        return "Yesterday";
    if (target.getFullYear() === today.getFullYear())
        return target.toLocaleString("en-US", { month: "long", day: "numeric" });
    return target.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function groupChatsByDate(chats: ChatRow[]): { label: string; items: ChatRow[] }[]
{
    let groups: { label: string; items: ChatRow[] }[] = [];
    let lookup = new Map<string, ChatRow[]>();
    for (let chat of chats)
    {
        let label = getDateGroupLabel(new Date(chat.updated_at));
        let bucket = lookup.get(label);
        if (!bucket)
        {
            bucket = [];
            lookup.set(label, bucket);
            groups.push({ label, items: bucket });
        }
        bucket.push(chat);
    }
    return groups;
}

interface ChatSidebarProps extends NSARouterMakerResult, InferenceRouterProps
{
    target_type: InferenceChatTargetType;
    target_id: string;
    chat_id: string;
    onNewChat: () => void;
    onSelect: (chat: ChatRow) => void;
}

interface ChatSidebarState
{
    open: boolean;
    chats: ChatRow[];
    page: number;
    total: number;
    loading: boolean;
    initialized: boolean;
}

class ChatSidebar extends Component<ChatSidebarProps, ChatSidebarState>
{
    static mapTargetTypeToChatInferencerType(target_type: InferenceChatTargetType): ChatInferencerType | null
    {
        switch (target_type)
        {
            case InferenceChatTargetType.Model:
                return ChatInferencerType.MODEL;
            case InferenceChatTargetType.LoadBalancer:
                return ChatInferencerType.LOAD_BALANCER;
            case InferenceChatTargetType.Failover:
                return ChatInferencerType.FAILOVER;
            case InferenceChatTargetType.Race:
                return ChatInferencerType.RACE;
            default:
                return null;
        }
    }

    private HistoryListRef = createRef<HTMLUListElement>();

    constructor(props: ChatSidebarProps)
    {
        super(props);
        this.state = {
            open: false,
            chats: [],
            page: 0,
            total: 0,
            loading: false,
            initialized: false,
        };
        this.onScroll = this.onScroll.bind(this);
    }

    override componentDidUpdate(prev: ChatSidebarProps)
    {
        if (prev.target_id !== this.props.target_id || prev.target_type !== this.props.target_type)
            this.setState({ chats: [], page: 0, total: 0, initialized: false }, () =>
            {
                if (this.state.open)
                    this.loadChats();
            });
    }

    public setOpen(open: boolean)
    {
        this.setState(() => ({ open }), () =>
        {
            if (open && !this.state.initialized)
                this.loadChats();
        });
    }

    private async loadChats()
    {
        if (this.state.loading)
            return;
        let next_page = this.state.page + 1;
        if (this.state.initialized && this.state.chats.length >= this.state.total)
            return;

        this.setState((prev) => ({ ...prev, loading: true }));
        try
        {
            let meta = new NamirasoftInferenceMetaDatabase();
            let filters: FilterItem[] = [];
            let inferencer_type = ChatSidebar.mapTargetTypeToChatInferencerType(this.props.target_type);
            if (inferencer_type !== null)
            {
                filters.push(new FilterItem(
                    meta.tables.chat,
                    meta.tables.chat.columns.inferencer_type,
                    false,
                    FilterItemOperator.all.equals,
                    inferencer_type,
                ));
                filters.push(new FilterItem(
                    meta.tables.chat,
                    meta.tables.chat.columns.inferencer_id,
                    false,
                    FilterItemOperator.all.equals,
                    this.props.target_id,
                ));
            }
            let response = await this.props.servers.inference.chat.List(filters, next_page, PAGE_SIZE, [], null);
            this.setState((prev) => ({
                ...prev,
                chats: [...prev.chats, ...response.rows],
                page: next_page,
                total: response.count,
                loading: false,
                initialized: true,
            }));
        }
        catch
        {
            // The server already reported the error through the notifier.
            this.setState((prev) => ({ ...prev, loading: false, initialized: true }));
        }
    }

    private onScroll(event: UIEvent<HTMLUListElement>)
    {
        let el = event.currentTarget;
        if (this.state.loading) return;
        if (this.state.chats.length >= this.state.total) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64)
            this.loadChats();
    }

    override render()
    {
        return (
            <div className={`${Styles.ns_sidebar} ${this.state.open ? Styles.open : ""}`}>
                <div className={Styles.ns_sidebar_content}>
                    <div className={Styles.ns_sidebar_header}>
                        <NSButton
                            title={this.state.open ? "New Chat" : ""}
                            onClick={{
                                showLoading: false,
                                action: () => this.props.onNewChat()
                            }}
                            classList={[Styles.ns_sidebar_btn]}
                            icon={{ src: "https://static.namirasoft.com/image/concept/new-chat/blue.svg", alt: "New Chat" }}
                        />

                        <NSLine classList={["w-100 bg-secondary"]} />

                        <NSButton
                            title={this.state.open ? "Chat History" : ""}
                            onClick={{
                                showLoading: false,
                                action: () => this.setOpen(!this.state.open)
                            }}
                            classList={[Styles.ns_sidebar_btn]}
                            icon={{ src: "https://static.namirasoft.com/image/concept/history/blue.svg", alt: "Chat History" }}
                        />
                    </div>

                    {
                        this.state.open &&
                        <ul ref={this.HistoryListRef} className={Styles.ns_history_list} onScroll={this.onScroll}>
                            {
                                groupChatsByDate(this.state.chats).map((group) => (
                                    <li key={group.label} className={Styles.ns_history_group}>
                                        <div className={Styles.ns_history_group_title}>{group.label}</div>
                                        <ul className={Styles.ns_history_group_list}>
                                            {
                                                group.items.map((chat) => (
                                                    <li
                                                        key={chat.id}
                                                        className={`${Styles.ns_history_item} ${this.props.chat_id === chat.id ? Styles.selected : ""}`}
                                                        onClick={() => this.props.onSelect(chat)}
                                                    >
                                                        <div className={Styles.ns_history_item_content}>
                                                            {chat.id}
                                                        </div>
                                                    </li>
                                                ))
                                            }
                                        </ul>
                                    </li>
                                ))
                            }
                            {
                                this.state.loading &&
                                <li className={Styles.ns_history_item}>
                                    <NSLoading />
                                </li>
                            }
                        </ul>
                    }
                </div>
            </div>
        );
    }
}

export default ChatSidebar;